import React, { useState, useMemo, useEffect, useRef } from 'react';
import { mockLessons, Lesson } from '../data/mockLessons';
import { LessonCard } from '../components/LessonCard';
import { SpeechRecorder } from '../components/SpeechRecorder';
import { FeedbackPanel } from '../components/FeedbackPanel';
import { Mic2, Filter, Sparkles, ChevronLeft, ChevronRight, Calendar, LogOut, User, Globe, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import heroImage from '../assets/speakeasy-hero.png';
import { useAuthStore } from '../store/authStore';
import { LoginModal } from '../components/LoginModal';
import { analyzeSpeech } from '../utils/scoring';
import { client } from '../lib/youbase';
import { getTranslation, getUITranslation, isRTL, detectBrowserLanguage } from '../utils/translation';
import { SUPPORTED_LANGUAGES } from '../data/translations/languages';

export const SpeakingPractice: React.FC = () => {
  const [selectedLesson, setSelectedLesson] = useState<Lesson>(mockLessons[0]);
  const [currentTranscript, setCurrentTranscript] = useState<string | null>(null);
  const [filterLevel, setFilterLevel] = useState<'All' | 'Beginner' | 'Intermediate' | 'Advanced'>('All');
  const [currentWeek, setCurrentWeek] = useState(1);
  const { user, signOut } = useAuthStore();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState(detectBrowserLanguage());
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languages = SUPPORTED_LANGUAGES;
  const practiceAreaRef = useRef<HTMLDivElement>(null);
  
  const handleLessonSelect = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setCurrentTranscript(null);
    practiceAreaRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRecordingComplete = async (transcript: string) => {
    setCurrentTranscript(transcript);
    
    if (user) {
      const analysis = analyzeSpeech(transcript, selectedLesson.prompt);
      try {
        await client.api.fetch('/api/progress', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lesson_id: selectedLesson.id,
            score: analysis.score,
            transcript: transcript
          })
        });
      } catch (error) {
        console.error('Failed to save progress:', error);
      }
    }
  };

  const filteredLessons = useMemo(() => {
    return mockLessons.filter(lesson => {
      const matchesLevel = filterLevel === 'All' || lesson.level === filterLevel;
      return matchesLevel;
    });
  }, [filterLevel]);

  // Weekly Schedule Logic
  const lessonsPerWeek = 7;
  const totalWeeks = Math.ceil(filteredLessons.length / lessonsPerWeek);
  
  const currentWeekLessons = useMemo(() => {
    const startIndex = (currentWeek - 1) * lessonsPerWeek;
    return filteredLessons.slice(startIndex, startIndex + lessonsPerWeek);
  }, [filteredLessons, currentWeek]);

  const handlePrevWeek = () => {
    if (currentWeek > 1) setCurrentWeek(prev => prev - 1);
  };

  const handleNextWeek = () => {
    if (currentWeek < totalWeeks) setCurrentWeek(prev => prev + 1);
  };

  // Reset week when filter changes
  useEffect(() => {
    setCurrentWeek(1);
  }, [filterLevel]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 selection:text-blue-900">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50 h-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-600/20">
              <Mic2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-slate-900">SpeakEasy</span>
          </div>
          
          {/* Centered Language Selector */}
          <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-blue-600 animate-pulse">
               <span className="text-sm font-medium">
                 {getUITranslation('chooseALanguage', targetLanguage)}
               </span>
               <ArrowRight className="w-5 h-5" />
            </div>
            {/* Language Selector */}
            <div className="relative">
              <button 
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 text-slate-500 hover:text-blue-600 transition-colors rounded-full hover:bg-blue-50"
                title="Change Translation Language"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">{getUITranslation('language', targetLanguage)}</span>
              </button>
              
              {isLanguageMenuOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-200">
                  <div className="px-4 py-2 border-b border-slate-50 mb-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{getUITranslation('chooseALanguage', targetLanguage)}</span>
                  </div>
                  {languages.map(lang => (
                    <button
                      key={lang}
                      onClick={() => {
                        setTargetLanguage(lang);
                        setIsLanguageMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        targetLanguage === lang 
                          ? 'text-blue-600 bg-blue-50 font-medium' 
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-50 rounded-full border border-slate-200">
                  <User className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-xs font-medium text-slate-700">{user.email?.split('@')[0]}</span>
                </div>
                <button 
                  onClick={() => signOut()}
                  className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors"
                  title={getUITranslation('signOut', targetLanguage)}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsLoginOpen(true)}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-xs font-medium hover:bg-blue-700 transition-all shadow-md shadow-blue-600/20"
              >
                {getUITranslation('signIn', targetLanguage)}
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Hero Section - Huge Image */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 shadow-lg shadow-blue-900/5">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/50 via-transparent to-transparent"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row gap-0 items-center">
            <div className="space-y-3 flex-1 min-w-0 z-10 p-6 md:p-8 md:pr-0">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-100/50 border border-blue-200 text-blue-700 text-[10px] font-bold uppercase tracking-wider">
                <Sparkles className="w-3 h-3" />
                <span>{getUITranslation('aiPowered', targetLanguage)}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold leading-tight text-slate-900">
                {getUITranslation('masterEnglishNaturally', targetLanguage)}
              </h1>
              <p className="text-slate-600 text-sm max-w-md leading-relaxed">
                {getUITranslation('practiceSpeakingWithRealTimeAiFeedback', targetLanguage)}
              </p>
            </div>
            
            {/* Hero Visual - Huge Image */}
            <div className="relative flex-1 w-full h-64 md:h-80 lg:h-96 flex items-center justify-center md:justify-end overflow-hidden py-12">
              <div className="absolute inset-0 bg-blue-500 rounded-full blur-3xl opacity-10 animate-pulse transform translate-x-1/4"></div>
              <img 
                src={heroImage} 
                alt="Speaking Practice" 
                className="relative w-full h-full object-contain object-center drop-shadow-2xl animate-float"
              />
            </div>
          </div>
        </section>

        {/* Practice Area - Compact Grid */}
        <section ref={practiceAreaRef} className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <SpeechRecorder 
              targetPrompt={selectedLesson.prompt}
              translation={getTranslation(selectedLesson.prompt, targetLanguage)}
              isRTL={isRTL(targetLanguage)}
              targetLanguage={targetLanguage}
              onRecordingComplete={handleRecordingComplete}
            />
          </div>
          <div className="lg:col-span-5 h-full min-h-[300px]">
            <FeedbackPanel 
              transcript={currentTranscript}
              targetPrompt={selectedLesson.prompt}
              targetLanguage={targetLanguage}
            />
          </div>
        </section>

        {/* Lesson Browser - Compact */}
        <section className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-display font-bold text-slate-900">{getUITranslation('curriculum', targetLanguage)}</h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex p-0.5 bg-white border border-slate-200 rounded-full shadow-sm overflow-x-auto">
                {(['All', 'Beginner', 'Intermediate', 'Advanced'] as const).map((level) => (
                  <button
                    key={level}
                    onClick={() => setFilterLevel(level)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                      filterLevel === level
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {getUITranslation(level.toLowerCase(), targetLanguage)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="relative flex items-center justify-center mb-4">
              <div className="absolute left-0 flex items-center gap-2 text-slate-800 font-bold text-sm">
                <Calendar className="w-4 h-4 text-blue-500" />
                <span>{getUITranslation('unit', targetLanguage)} {currentWeek} {getUITranslation('of', targetLanguage)} {totalWeeks}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handlePrevWeek}
                  disabled={currentWeek === 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors text-slate-600 text-sm font-medium"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>{getUITranslation('previous', targetLanguage)}</span>
                </button>
                <button
                  onClick={handleNextWeek}
                  disabled={currentWeek === totalWeeks}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors text-slate-600 text-sm font-medium"
                >
                  <span>{getUITranslation('nextLesson', targetLanguage)}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <motion.div 
              variants={container}
              initial="hidden"
              animate="show"
              key={currentWeek}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              <AnimatePresence mode='wait'>
                {currentWeekLessons.map((lesson) => (
                  <motion.div key={lesson.id} variants={item}>
                    <LessonCard
                      lesson={lesson}
                      isSelected={selectedLesson.id === lesson.id}
                      onClick={() => handleLessonSelect(lesson)}
                      targetLanguage={targetLanguage}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
            
            {currentWeekLessons.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                <p>{getUITranslation('noLessonsFound', targetLanguage)}</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer - Compact */}
      <footer className="bg-white border-t border-slate-200 mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-xs">
            © {new Date().getFullYear()} SpeakEasy. All rights reserved.
          </p>
          <p className="text-slate-300 text-[10px] mt-1">
            Special thanks to <a href="https://www.youtube.com/@DrNellieDeutsch" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Dr. Nellie Deutsch</a>.
          </p>
        </div>
      </footer>

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </div>
  );
};
